import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Button, Popconfirm } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import API, { OrchestratorAPI } from "../../common/api/axios";
import { TENANT_HEADERS } from "../../common/config";
import { useAppData } from "../../app/appContext";
import { formatSlotDisplay } from "../../common/utils/slot";
import {
  flattenStaticPickWallSlots,
  StaticPickWallSlot,
} from "../../common/utils/staticPickWall";
import { resolveInwardGate } from "../../common/utils/gateMatch";
import {
  buildBinToTaskIdMap,
  buildHjiBinLocationPayload,
  buildLegacyInwardOrderPayload,
  buildRemainderOrderPayload,
  computeRemainderQuantity,
  findMatchingPendingOrders,
  getOrderSkuRows,
  inwardTaskMatchesBinSku,
  isInwardGateEligible,
  normalizeBinId,
  slotKeyDot,
  targetOrderQuantityForSku,
  SlotPosition,
} from "../../common/utils/inward";
import { cleanScan } from "../../common/utils/scan";
import { isAlreadyCompleteError, serverErrorMessage } from "../../common/utils/errors";
import { toastError, toastSuccess } from "../../common/utils/toast";
import StepShell from "../../components/StepShell";
import StatusBanner from "../../components/StatusBanner";
import ScanField from "../../components/ScanField";
import QuantityStepper from "../../components/QuantityStepper";
import ConfirmModal, { ConfirmRow } from "../../components/ConfirmModal";
import { colors } from "../../app/styles";

const ORCH_POLL_MS = 5000;
const PENDING_POLL_MS = 5000;
const STATION_POLL_MS = 3000;
const STATIC_MAX_ATTEMPTS = 20;
const STATIC_RETRY_MS = 1000;
// How long a just-submitted order stays hidden from the pending list while its
// bin-store completes in the background. A fail-visible cap: if the store hasn't
// cleared server-side by now, the order re-surfaces rather than being stranded.
const PROCESSING_GRACE_MS = 45000;

interface InwardGate {
  x: number;
  y: number;
  z: number;
  slot: SlotPosition;
  isDisabled: boolean;
  processingQueue: any[];
}

interface ResolvedSku {
  skuId: string;
  isStatic: boolean;
  inductQuantity: number;
}

/** A submitted inward whose completion is being watched in the background. */
interface InwardJob {
  id: string;
  slot: SlotPosition;
  binId: string;
  skuId: string;
  kind: "dynamic" | "static";
  label: string;
  /** The order this store was submitted against (HJI) — suppresses its re-poll. */
  orderRef: string;
  /** Epoch ms at submit, for the fail-visible processing grace window. */
  submittedAt: number;
}

type SelectedGate = { kind: "dynamic" | "static"; slot: SlotPosition };

// The whole inward is now filled on one page; `field` only tracks which field
// takes wedge focus next, so a scan auto-advances slot → container → sku → qty.
type InwardField = "slot" | "container" | "sku" | "qty";

const Center = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 20px;
  text-align: center;
  color: ${colors.textMuted};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: ${colors.primaryDark};
`;

const FieldCard = styled.div`
  border: 1px solid ${colors.cardBorder};
  border-radius: 12px;
  background: #ffffff;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ResolvedRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const ResolvedLabel = styled.span`
  font-size: 13px;
  color: ${colors.textMuted};
`;

const ResolvedValue = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${colors.primaryDark};
  text-align: right;
  overflow-wrap: anywhere;
`;

const QtyExpected = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;

  strong {
    font-size: 30px;
    font-weight: 800;
    color: ${colors.primaryDark};
    line-height: 1;
  }
  span {
    font-size: 14px;
    color: ${colors.textMuted};
  }
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${colors.textMuted};
`;

const ErrorPanel = styled.div`
  border: 1px solid #ffa39e;
  background: #fff1f0;
  border-radius: 12px;
  padding: 10px 12px;
  color: #a8071a;
  font-weight: 600;
`;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default function Inward() {
  const navigate = useNavigate();
  const { configName } = useAppData();
  const isHji = configName?.trim().toUpperCase() === "HJI";

  const [loading, setLoading] = useState(true);
  const [inwardActive, setInwardActive] = useState(false);
  const [exiting, setExiting] = useState(false);

  const [stations, setStations] = useState<any[]>([]);
  const [staticSlots, setStaticSlots] = useState<StaticPickWallSlot[]>([]);
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);

  const [field, setField] = useState<InwardField>("slot");
  const [showConfirm, setShowConfirm] = useState(false);
  const [slotScan, setSlotScan] = useState("");
  const [slotError, setSlotError] = useState<string | null>(null);

  const [selectedGate, setSelectedGate] = useState<SelectedGate | null>(null);
  const [staticBinId, setStaticBinId] = useState("");

  const [containerScan, setContainerScan] = useState("");
  const [skuScan, setSkuScan] = useState("");
  const [resolvedSku, setResolvedSku] = useState<ResolvedSku | null>(null);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuError, setSkuError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [creatingRemainder, setCreatingRemainder] = useState(false);
  const [remainderCreated, setRemainderCreated] = useState(false);

  // Submitting no longer blocks the screen: each submitted inward is tracked here
  // and its completion is watched in the background while the operator scans on.
  const [inProgress, setInProgress] = useState<InwardJob[]>([]);
  const jobIdRef = useRef(0);

  const mountedRef = useRef(true);
  const watchTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  const dynamicGates: InwardGate[] = useMemo(
    () =>
      stations.flatMap((s: any) =>
        (Array.isArray(s?.gates) ? s.gates : []).map((g: any) => ({
          x: g.slot.x,
          y: g.slot.y,
          z: g.slot.z,
          slot: g.slot,
          isDisabled: g.isDisabled === true,
          processingQueue: Array.isArray(g.processingQueue) ? g.processingQueue : [],
        }))
      ),
    [stations]
  );

  // ── Data fetching ──

  const fetchStations = useCallback(async () => {
    try {
      const res = await API.get("PickStation/inwardStations", {
        headers: TENANT_HEADERS,
      });
      if (res.status === 200 && Array.isArray(res.data) && mountedRef.current) {
        setStations(res.data);
      }
    } catch {
      // keep prior
    }
  }, []);

  const fetchStaticSlots = useCallback(async () => {
    try {
      const res = await API.get("StaticPickWall", { headers: TENANT_HEADERS });
      if (res.status === 200 && mountedRef.current) {
        setStaticSlots(flattenStaticPickWallSlots(res.data));
      }
    } catch {
      if (mountedRef.current) {
        setStaticSlots([]);
      }
    }
  }, []);

  const checkOrchestration = useCallback(async () => {
    try {
      const res = await OrchestratorAPI.get("orchestrations", {
        headers: TENANT_HEADERS,
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        const active = res.data.find(
          (orch: any) =>
            orch.operationMode === "Inward" &&
            orch.status !== "Completed" &&
            new Date(orch.creationDate) >= oneDayAgo
        );
        if (mountedRef.current) {
          setInwardActive(!!active);
        }
      }
    } catch {
      if (mountedRef.current) {
        setInwardActive(false);
      }
    }
  }, []);

  const fetchPendingOrders = useCallback(async () => {
    try {
      const res = await API.get("InwardOrder/pending", { headers: TENANT_HEADERS });
      if (res.status === 200 && Array.isArray(res.data) && mountedRef.current) {
        setPendingOrders(res.data);
      }
    } catch {
      // keep prior
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    Promise.all([
      fetchStations(),
      fetchStaticSlots(),
      checkOrchestration(),
    ]).finally(() => {
      if (mountedRef.current) {
        setLoading(false);
      }
    });
    return () => {
      mountedRef.current = false;
      watchTimersRef.current.forEach((t) => clearTimeout(t));
      watchTimersRef.current.clear();
    };
  }, [fetchStations, fetchStaticSlots, checkOrchestration]);

  useEffect(() => {
    const interval = setInterval(checkOrchestration, ORCH_POLL_MS);
    return () => clearInterval(interval);
  }, [checkOrchestration]);

  // Pending inward orders resolve the HJI submit target AND gate the Exit action
  // (Exit is only offered once no inward work remains).
  useEffect(() => {
    if (!inwardActive) {
      setPendingOrders([]);
      return;
    }
    fetchPendingOrders();
    const interval = setInterval(fetchPendingOrders, PENDING_POLL_MS);
    return () => clearInterval(interval);
  }, [inwardActive, fetchPendingOrders]);

  // Keep dynamic-gate queue state fresh while scanning.
  useEffect(() => {
    if (!inwardActive) {
      return;
    }
    const interval = setInterval(fetchStations, STATION_POLL_MS);
    return () => clearInterval(interval);
  }, [inwardActive, fetchStations]);

  // ── Flow helpers ──

  const resetFlow = useCallback(() => {
    setField("slot");
    setShowConfirm(false);
    setSlotScan("");
    setSlotError(null);
    setSelectedGate(null);
    setStaticBinId("");
    setContainerScan("");
    setSkuScan("");
    setResolvedSku(null);
    setSkuError(null);
    setQuantity(1);
    setSubmitError(null);
    setRemainderCreated(false);
  }, []);

  // Re-scanning the slot restarts the placement, so wipe the SKU/qty captured
  // against any prior slot.
  const clearAfterSlot = () => {
    setSkuScan("");
    setResolvedSku(null);
    setSkuError(null);
    setQuantity(1);
    setSubmitError(null);
    setRemainderCreated(false);
  };

  const handleSlotScan = async () => {
    const scanned = cleanScan(slotScan);
    setSlotError(null);
    const match = resolveInwardGate(scanned, dynamicGates, staticSlots);
    if (!match) {
      setSlotError(
        `Slot ${formatSlotDisplay(scanned) || scanned} is not an active inward gate`
      );
      return;
    }
    if (!isInwardGateEligible(match.kind === "dynamic" ? match.gate : match.slot, inwardActive)) {
      setSlotError(
        `Slot ${formatSlotDisplay(scanned) || scanned} is not available for inward`
      );
      return;
    }

    if (match.kind === "dynamic") {
      setSelectedGate({ kind: "dynamic", slot: match.gate.slot });
      setStaticBinId("");
      setContainerScan("");
      clearAfterSlot();
      setField("container");
      return;
    }

    // Static slot: pre-resolve its assigned bin.
    const slot: SlotPosition = { x: match.slot.x, y: match.slot.y, z: match.slot.z };
    try {
      const res = await API.get(`Slot/slot/${slot.x}/${slot.y}/${slot.z}`, {
        headers: TENANT_HEADERS,
      });
      const binId =
        typeof res.data?.binId === "string" ? res.data.binId.trim() : "";
      if (!binId) {
        setSlotError("No tote is assigned to this static slot.");
        return;
      }
      setStaticBinId(binId);
      setSelectedGate({ kind: "static", slot });
      setContainerScan("");
      clearAfterSlot();
      setField("container");
    } catch (error) {
      setSlotError(serverErrorMessage(error, "Unable to load the static slot tote ID."));
    }
  };

  // Best-effort SKU master-data lookup. Returns the resolved SKU (or null) so the
  // caller can decide how to proceed. Unlike the desktop, HJI does NOT hard-gate on
  // this call — the pending order is the source of truth (MaterialInward.tsx:1330).
  const lookupSku = async (rawSkuId: string): Promise<ResolvedSku | null> => {
    const skuId = cleanScan(rawSkuId);
    if (!skuId) {
      setResolvedSku(null);
      setSkuError(null);
      return null;
    }
    setSkuLoading(true);
    setSkuError(null);
    try {
      const res = await API.get(`MasterData/sku/${encodeURIComponent(skuId)}`, {
        headers: TENANT_HEADERS,
      });
      if (res.status === 200 && res.data) {
        const induct = Number(res.data?.inductQuantity);
        const sku: ResolvedSku = {
          skuId: res.data?.skuId ?? skuId,
          isStatic: res.data?.isStatic === true,
          inductQuantity: Number.isFinite(induct) && induct > 0 ? induct : 0,
        };
        setResolvedSku(sku);
        setSkuError(null);
        return sku;
      }
      setResolvedSku(null);
      setSkuError("SKU ID not found.");
      return null;
    } catch (error) {
      setResolvedSku(null);
      setSkuError(serverErrorMessage(error, "Unable to load SKU details."));
      return null;
    } finally {
      if (mountedRef.current) {
        setSkuLoading(false);
      }
    }
  };

  // A resolved SKU seeds the quantity from the order and hands wedge focus to the
  // quantity stepper; the operator confirms from the same page.
  const seedQuantityAndAdvance = (seedQty: number) => {
    setQuantity(seedQty >= 1 ? Math.round(seedQty) : 1);
    setSubmitError(null);
    setRemainderCreated(false);
    setField("qty");
  };

  // Scan/commit the SKU. HJI advances when a pending order matches (seed = order
  // qty); legacy advances when master-data resolves (seed = induct qty).
  const handleSkuScan = async (rawSkuId: string) => {
    const scanned = cleanScan(rawSkuId);
    if (!scanned) {
      return;
    }
    const sku = await lookupSku(rawSkuId);

    // Static-SKU routing guard — parity with the desktop's getSlotState /
    // requiresSkuRouting (MaterialInward.tsx:524-534): when the warehouse has
    // static slots and master data resolved the SKU, its static-ness must match
    // the kind of slot that was scanned. Skipped when master data missed
    // (sku === null) so HJI SKUs absent from master data are not falsely rejected.
    if (sku && selectedGate && staticSlots.length > 0) {
      const slotIsStatic = selectedGate.kind === "static";
      if (sku.isStatic !== slotIsStatic) {
        setSkuError(
          slotIsStatic
            ? `Slot ${formatSlotDisplay(
                selectedGate.slot
              )} is a static slot, but ${sku.skuId} is not a static SKU.`
            : `${sku.skuId} is a static SKU and must be inwarded to a static slot — ${formatSlotDisplay(
                selectedGate.slot
              )} is a dynamic gate.`
        );
        return;
      }
    }

    if (isHji) {
      const orders = findMatchingPendingOrders(pendingOrders, scanned);
      if (orders.length === 0) {
        setSkuError("No pending inward order found for this SKU.");
        return;
      }
      setSkuError(null);
      // Review needs a resolvedSku to render; synthesize one if master data missed.
      if (!sku) {
        setResolvedSku({ skuId: scanned, isStatic: false, inductQuantity: 0 });
      }
      // Seed strictly from the pending order's sku.quantity — the same field the
      // desktop reads (InwardModal.tsx targetOrderQuantity). No induct-qty fallback.
      const orderQty = targetOrderQuantityForSku(orders[0], scanned);
      seedQuantityAndAdvance(orderQty && orderQty > 0 ? orderQty : 1);
      return;
    }

    // Legacy: master data is the gate (there is no pending order to seed from).
    if (!sku) {
      return; // lookupSku already surfaced the error
    }
    seedQuantityAndAdvance(sku.inductQuantity > 0 ? sku.inductQuantity : 1);
  };

  // ── Completion watch ──

  const completeInwardTask = async (operatorTaskId: string): Promise<boolean> => {
    try {
      await API.put(
        `PickStation/completeInwardTask/${encodeURIComponent(operatorTaskId)}`,
        undefined,
        { headers: TENANT_HEADERS }
      );
      return true;
    } catch (error) {
      if (isAlreadyCompleteError(error)) {
        return true; // desktop or another device finished it first — benign
      }
      return false;
    }
  };

  const removeJob = (id: string) => {
    const timer = watchTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      watchTimersRef.current.delete(id);
    }
    if (mountedRef.current) {
      setInProgress((prev) => prev.filter((j) => j.id !== id));
    }
  };

  const watchDynamicCompletion = (job: InwardJob) => {
    const key = slotKeyDot(job.slot);
    let hasSeen = false;
    let capturedTaskId: string | undefined;

    const tick = async () => {
      if (!mountedRef.current) {
        return;
      }
      try {
        const res = await API.get("PickStation/inwardStations", {
          headers: TENANT_HEADERS,
        });
        const data = Array.isArray(res.data) ? res.data : [];
        if (mountedRef.current) {
          setStations(data);
        }
        const gate = data
          .flatMap((s: any) => (Array.isArray(s?.gates) ? s.gates : []))
          .find((g: any) => slotKeyDot(g.slot) === key);
        const queue: any[] = gate?.processingQueue ?? [];
        const matching = queue.find(
          (item) => normalizeBinId(item.binId) === normalizeBinId(job.binId)
        );

        if (matching) {
          if (matching.operatorTaskId) {
            capturedTaskId = matching.operatorTaskId;
          }
          hasSeen = true;
        } else if (hasSeen && queue.length === 0) {
          // Bin was observed and has now left the gate → store complete.
          let taskId = capturedTaskId;
          if (!taskId) {
            try {
              const pend = await API.get("InwardOrder/pending", {
                headers: TENANT_HEADERS,
              });
              taskId = buildBinToTaskIdMap(
                Array.isArray(pend.data) ? pend.data : []
              ).get(normalizeBinId(job.binId));
            } catch {
              // retry on the next tick
            }
          }
          if (taskId) {
            const ok = await completeInwardTask(taskId);
            if (ok) {
              toastSuccess(`Inward stored — ${job.label}`);
              removeJob(job.id);
              return;
            }
          }
          // taskId not resolvable yet (or completion failed) — keep polling.
        }
      } catch {
        // transient — keep polling
      }
      watchTimersRef.current.set(job.id, setTimeout(tick, STATION_POLL_MS));
    };

    tick();
  };

  const watchStaticCompletion = async (job: InwardJob) => {
    for (let attempt = 0; attempt < STATIC_MAX_ATTEMPTS; attempt += 1) {
      if (!mountedRef.current) {
        return;
      }
      try {
        const res = await API.get("PickStation/inwardTasks", {
          headers: TENANT_HEADERS,
        });
        if (Array.isArray(res.data)) {
          const task = res.data.find((t: any) =>
            inwardTaskMatchesBinSku(t, job.binId, job.skuId)
          );
          if (task?.id) {
            const ok = await completeInwardTask(task.id);
            if (ok) {
              toastSuccess(`Inward stored — ${job.label}`);
            } else {
              toastError(`Failed to complete inward — ${job.label}`);
            }
            removeJob(job.id);
            return;
          }
        }
      } catch {
        // retry
      }
      if (attempt < STATIC_MAX_ATTEMPTS - 1) {
        await sleep(STATIC_RETRY_MS);
      }
    }
    toastError(
      `Could not auto-complete static inward — ${job.label}. Verify it in admin.`
    );
    removeJob(job.id);
  };

  // ── Submit ──

  // Orders whose bin-store is still completing in the background are hidden for a
  // grace window so the just-submitted order doesn't re-appear as new work (and
  // can't be double-submitted). After the window it re-surfaces (fail-visible).
  const nowMs = Date.now();
  const processingRefs = new Set(
    inProgress
      .filter((j) => j.orderRef && nowMs - j.submittedAt < PROCESSING_GRACE_MS)
      .map((j) => j.orderRef)
  );
  const outstandingPending = pendingOrders.filter(
    (o) => !processingRefs.has(o.inwardOrderReferenceId)
  );
  const matchingOrders = isHji
    ? findMatchingPendingOrders(outstandingPending, skuScan)
    : [];
  const selectedOrder = matchingOrders[0] ?? null;
  const targetQty = selectedOrder
    ? targetOrderQuantityForSku(selectedOrder, cleanScan(skuScan))
    : null;
  // Batch(es) on the matched order for the scanned SKU (parity with the desktop
  // InwardModal's selectedBatchText).
  const batchText = useMemo(() => {
    if (!selectedOrder) {
      return "";
    }
    const sku = cleanScan(skuScan);
    const batches = Array.from(
      new Set(
        getOrderSkuRows(selectedOrder)
          .filter(({ sku: row }) => row.skuId?.trim() === sku)
          .map(({ sku: row }) => row.batchId?.trim())
          .filter((b): b is string => Boolean(b))
      )
    );
    return batches.join(", ");
  }, [selectedOrder, skuScan]);
  const remainderQty =
    selectedGate?.kind === "dynamic"
      ? computeRemainderQuantity(quantity, targetQty, false)
      : 0;

  const handleSubmit = async () => {
    if (!selectedGate || !resolvedSku) {
      return;
    }
    const slot = selectedGate.slot;
    const isStatic = selectedGate.kind === "static";
    const container = cleanScan(containerScan);
    const sku = resolvedSku.skuId;
    const toteId = isStatic ? staticBinId : container;

    setSubmitting(true);
    setSubmitError(null);
    try {
      if (isHji) {
        if (!selectedOrder) {
          setSubmitError("No pending inward order found for this SKU.");
          return;
        }
        await API.put(
          `InwardOrder/${encodeURIComponent(selectedOrder.inwardOrderReferenceId)}/binLocation`,
          buildHjiBinLocationPayload({
            slot,
            toteId,
            skuId: sku,
            caseId: isStatic ? container : "",
            inwardedQuantity: quantity,
          }),
          { headers: TENANT_HEADERS }
        );
        setPendingOrders((prev) =>
          prev.filter(
            (o) => o.inwardOrderReferenceId !== selectedOrder.inwardOrderReferenceId
          )
        );
      } else {
        await API.post(
          "InwardOrder",
          buildLegacyInwardOrderPayload({
            slot,
            skuId: sku,
            quantity,
            containerId: container,
            isStatic,
            binId: toteId,
            nowIso: new Date().toISOString(),
          }),
          { headers: TENANT_HEADERS }
        );
      }

      // Fire-and-forget completion watch: the operator moves straight to the next
      // inward while the bin-left-gate → completeInwardTask handshake runs behind.
      const job: InwardJob = {
        id: String(++jobIdRef.current),
        slot,
        binId: toteId,
        skuId: sku,
        kind: isStatic ? "static" : "dynamic",
        label: `Slot ${formatSlotDisplay(slot)}`,
        orderRef: isHji ? selectedOrder?.inwardOrderReferenceId ?? "" : "",
        submittedAt: Date.now(),
      };
      setInProgress((prev) => [...prev, job]);
      if (isStatic) {
        void watchStaticCompletion(job);
      } else {
        watchDynamicCompletion(job);
      }
      toastSuccess(`Inward submitted — ${job.label}`);
      resetFlow();
    } catch (error) {
      setSubmitError(serverErrorMessage(error, "Failed to submit inward."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateRemainder = async () => {
    if (!selectedOrder || remainderQty <= 0 || creatingRemainder) {
      return;
    }
    setCreatingRemainder(true);
    try {
      await API.post(
        "InwardOrder",
        buildRemainderOrderPayload({
          skuId: resolvedSku?.skuId ?? cleanScan(skuScan),
          remainderQuantity: remainderQty,
          orchestrationId: selectedOrder.orchestrationId,
        }),
        { headers: TENANT_HEADERS }
      );
      setRemainderCreated(true);
      toastSuccess("Remainder inward order created");
    } catch (error) {
      toastError(serverErrorMessage(error, "Failed to create remainder order."));
    } finally {
      setCreatingRemainder(false);
    }
  };

  const handleExitInward = async () => {
    if (exiting || !inwardActive) {
      return;
    }
    setExiting(true);
    try {
      await OrchestratorAPI.post("orchestrations/exitInward", {}, {
        headers: TENANT_HEADERS,
      });
      await checkOrchestration();
      toastSuccess("Exit requested. Inward will stop after current operations.");
    } catch (error) {
      toastError(serverErrorMessage(error, "Failed to exit inward."));
      setExiting(false);
    }
  };

  // ── Render ──

  if (loading) {
    return <Center>Checking inward operation…</Center>;
  }

  if (!inwardActive) {
    return (
      <Center>
        <StatusBanner tone="info">
          No active inward operation. Start one from the desktop Operator UI,
          then scan a slot here.
        </StatusBanner>
        <Button size="large" onClick={() => navigate("/")}>
          Home
        </Button>
      </Center>
    );
  }

  // Single-page inward: slot, tote/case, SKU and quantity are all shown at once
  // (mirrors the desktop Operator UI). A scan auto-advances wedge focus to the
  // next field; the operator confirms via a review modal.
  const isStatic = selectedGate?.kind === "static";
  const gateResolved = !!selectedGate;
  const containerFilled = cleanScan(containerScan) !== "";
  const canConfirm =
    gateResolved &&
    containerFilled &&
    !!resolvedSku &&
    quantity >= 1 &&
    (!isStatic || staticBinId !== "") &&
    (!isHji || !!selectedOrder) &&
    !submitting;

  const confirmRows: ConfirmRow[] = [
    {
      label: "Slot",
      value: selectedGate ? formatSlotDisplay(selectedGate.slot) : "—",
    },
    {
      label: isStatic ? "Case ID" : "Tote ID",
      value: cleanScan(containerScan) || "—",
    },
  ];
  if (isStatic) {
    confirmRows.push({ label: "Static Bin", value: staticBinId || "—" });
  }
  confirmRows.push({ label: "SKU", value: resolvedSku?.skuId ?? "—" });
  if (isHji) {
    confirmRows.push({ label: "Batch ID", value: batchText || "—" });
  }
  if (targetQty !== null) {
    confirmRows.push({ label: "Expected Qty", value: targetQty });
  }
  confirmRows.push({ label: "Entered Qty", value: quantity, highlight: true });

  return (
    <>
      <StepShell
        title="Material Inward"
        subtitle="Scan slot, tote, and SKU"
        onBack={() => navigate("/")}
        actions={
          <>
            {outstandingPending.length === 0 ? (
              <Popconfirm
                title="Exit inward operation?"
                description="Ends the whole inward operation, not just one item. It stops after current work completes."
                okText="Exit"
                cancelText="Cancel"
                onConfirm={handleExitInward}
              >
                <Button size="large" danger loading={exiting}>
                  Exit Operation
                </Button>
              </Popconfirm>
            ) : (
              <Button
                size="large"
                onClick={resetFlow}
                disabled={!gateResolved && cleanScan(slotScan) === ""}
              >
                Reset
              </Button>
            )}
            <Button
              size="large"
              type="primary"
              disabled={!canConfirm}
              onClick={() => {
                setSubmitError(null);
                setShowConfirm(true);
              }}
            >
              Confirm
            </Button>
          </>
        }
      >
        {inProgress.length > 0 && (
          <StatusBanner tone="info">
            {inProgress.length} inward{inProgress.length === 1 ? "" : "s"} storing
            in the background — keep scanning.
          </StatusBanner>
        )}

        <FieldGroup>
          <FieldTitle>Scan Slot ID</FieldTitle>
          <FieldCard>
            <ScanField
              placeholder="e.g. 4-1-2"
              value={slotScan}
              onChange={(v) => {
                setSlotScan(v);
                if (slotError) {
                  setSlotError(null);
                }
              }}
              onScan={handleSlotScan}
              validateState={slotError ? "error" : gateResolved ? "ok" : "idle"}
              autoFocus={field === "slot"}
              focusSignal={field}
            />
            {selectedGate && (
              <ResolvedRow>
                <ResolvedLabel>Slot</ResolvedLabel>
                <ResolvedValue>
                  {formatSlotDisplay(selectedGate.slot)} ·{" "}
                  {isStatic ? "Static" : "Dynamic"}
                </ResolvedValue>
              </ResolvedRow>
            )}
          </FieldCard>
          {slotError && <ErrorPanel>{slotError}</ErrorPanel>}
        </FieldGroup>

        <FieldGroup>
          <FieldTitle>{isStatic ? "Scan Case ID" : "Scan Tote ID"}</FieldTitle>
          <FieldCard>
            {isStatic && staticBinId && (
              <ResolvedRow>
                <ResolvedLabel>Static Bin</ResolvedLabel>
                <ResolvedValue>{staticBinId}</ResolvedValue>
              </ResolvedRow>
            )}
            <ScanField
              placeholder={isStatic ? "CASE…" : "TOTE…"}
              value={containerScan}
              onChange={setContainerScan}
              onScan={(v) => {
                if (gateResolved && cleanScan(v) !== "") {
                  setField("sku");
                }
              }}
              autoFocus={field === "container"}
              focusSignal={field}
              disabled={!gateResolved || submitting}
            />
          </FieldCard>
        </FieldGroup>

        <FieldGroup>
          <FieldTitle>Scan SKU ID</FieldTitle>
          <FieldCard>
            <ScanField
              placeholder="SKU…"
              value={skuScan}
              onChange={(v) => {
                setSkuScan(v);
                setResolvedSku(null);
                setSkuError(null);
              }}
              onScan={(v) => handleSkuScan(v)}
              validateState={skuError ? "error" : resolvedSku ? "ok" : "idle"}
              autoFocus={field === "sku"}
              focusSignal={field}
              disabled={!gateResolved || !containerFilled || submitting}
            />
            {skuLoading && <Muted>Looking up SKU…</Muted>}
            {resolvedSku && (
              <ResolvedRow>
                <ResolvedLabel>SKU</ResolvedLabel>
                <ResolvedValue>
                  {resolvedSku.skuId} ·{" "}
                  {resolvedSku.isStatic ? "Static" : "Dynamic"}
                </ResolvedValue>
              </ResolvedRow>
            )}
          </FieldCard>
          {skuError && <ErrorPanel>{skuError}</ErrorPanel>}
        </FieldGroup>

        {isHji && (
          <FieldGroup>
            <FieldTitle>Batch</FieldTitle>
            <FieldCard>
              <ResolvedRow>
                <ResolvedLabel>Batch ID</ResolvedLabel>
                <ResolvedValue>{batchText || "—"}</ResolvedValue>
              </ResolvedRow>
            </FieldCard>
          </FieldGroup>
        )}

        <FieldGroup>
          <FieldTitle>Quantity</FieldTitle>
          <FieldCard>
            {isHji && targetQty !== null && (
              <QtyExpected>
                <strong>{targetQty}</strong>
                <span>Expected</span>
              </QtyExpected>
            )}
            <QuantityStepper
              value={quantity}
              min={1}
              onChange={setQuantity}
              disabled={!resolvedSku || submitting}
              active={field === "qty"}
            />
          </FieldCard>
          {isHji && remainderQty > 0 && (
            <div>
              <StatusBanner tone="warning">
                This inducts {quantity} of {targetQty}. Remainder {remainderQty}{" "}
                can be split into a separate inward order.
              </StatusBanner>
              <Button
                block
                style={{ marginTop: 8 }}
                loading={creatingRemainder}
                disabled={remainderCreated}
                onClick={handleCreateRemainder}
              >
                {remainderCreated
                  ? "Remainder order created"
                  : `Create remainder order (${remainderQty})`}
              </Button>
            </div>
          )}
        </FieldGroup>
      </StepShell>

      <ConfirmModal
        open={showConfirm}
        icon={<InboxOutlined />}
        title="Confirm Bin Inward"
        subtitle="Verify placement details before storing"
        rows={confirmRows}
        note="On confirm, the tote is stored and awaits bot retrieval."
        error={submitError}
        confirmLabel="Confirm"
        loading={submitting}
        onCancel={() => {
          setShowConfirm(false);
          setSubmitError(null);
        }}
        onConfirm={handleSubmit}
      />
    </>
  );
}
